import { Breadcrumb, Button, Input, Table, Tag } from "antd";
import { EditOutlined, HomeOutlined, SearchOutlined } from "@ant-design/icons";
import { Content } from "antd/es/layout/layout";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { _adr } from "../../../address";
import { NotFound } from "../../../sources/svg/svg";
import Loading from "../loader/loading";
import "../report/report.scss";

const PAGE_SIZE = 10;

/**
 * "در انتظار آپلود مدارک" list shared by sales / ownership / swap / in-person delivery.
 *
 * title           page title and breadcrumb
 * prefix          legacy class prefix ("sp" -> container-sp, sp-container) so existing page styles keep applying
 * query           (payload) => response
 * stateFilter     the by-state filter of that service, e.g. { purchasestate: [1, 15] }
 * commissionTitle the commission column title (sales: "سود شما از فروش", others: "پورسانت")
 * toRow           (item) => { trackcode, msisdn, type, price, commission, purchaseDate }
 * uploadPath      route of the upload page; the raw item is passed as router state
 */
const PendingDocsPage = ({ title, prefix, query, stateFilter, commissionTitle, toRow, uploadPath }) => {
	const nav = useNavigate();
	const [loading, setLoading] = useState(false);
	const [rows, setRows] = useState([]);
	const [page, setPage] = useState(1);
	const [total, setTotal] = useState(0);
	const [filter, setFilter] = useState("");

	// server paging for every service (swap / ownership / delivery used to load only the first 10)
	useEffect(() => {
		let cancelled = false;
		const load = async () => {
			setLoading(true);
			const response = await query({
				...stateFilter,
				search: { sort: "", filter: "", entriesperpage: PAGE_SIZE, page: page - 1, totalrowcount: 0 },
			});
			if (!cancelled && response) {
				setRows((response.Result ?? []).map((item, key) => ({ key, ...toRow(item), _raw: item })));
				setTotal(response.searchconfigmodel?.totalrowcount ?? response.Result?.length ?? 0);
			}
			if (!cancelled) setLoading(false);
		};
		load();
		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page]);

	const term = filter.trim().toLowerCase();
	const visible = term ? rows.filter((r) => ["trackcode", "msisdn", "type"].some((k) => String(r[k] ?? "").toLowerCase().includes(term))) : rows;

	const columns = [
		{ title: "کدپیگیری", dataIndex: "trackcode", key: "trackcode" },
		{ title: "شماره آپتل", dataIndex: "msisdn", key: "msisdn" },
		{ title: "نوع باندل", dataIndex: "type", key: "type", className: "report-wrap" },
		{ title: "قیمت (تومان)", dataIndex: "price", key: "price" },
		{ title: `${commissionTitle} (تومان)`, dataIndex: "commission", key: "commission" },
		{ title: "تاریخ خرید", dataIndex: "purchaseDate", key: "purchaseDate" },
		{
			title: "وضعیت",
			key: "status",
			render: () => (
				<Tag color="warning" className="report-status">
					نقص مدرک
				</Tag>
			),
		},
		{
			title: "",
			key: "action",
			render: (_, row) => (
				<Button size="small" type="primary" icon={<EditOutlined />} className="pending-fix-btn" onClick={() => nav(uploadPath, { state: row._raw })}>
					اصلاح مدارک
				</Button>
			),
		},
	];

	return (
		<div className={`container-main container-${prefix}`}>
			<div className="g-hold-title-of-page">
				<Breadcrumb className="breadcrumbs" items={[{ href: _adr.Root + _adr.Home, title: <HomeOutlined /> }, { title }]} />
				<div className="title-page">
					<h3>{title}</h3>
				</div>
			</div>
			<div className="g-layout-content">
				<Content className={`g-layout-main report-page ${prefix}-container`}>
					<section className="report-table">
						<header>
							<div className="report-table-title">
								<h4>درخواست‌های دارای نقص مدرک</h4>
								<span className="report-muted">{total} مورد</span>
							</div>
							<div className="report-table-tools">
								<Input
									placeholder="جستجو..."
									allowClear
									value={filter}
									onChange={(e) => setFilter(e.target.value)}
									prefix={<SearchOutlined />}
									className="report-filter"
								/>
							</div>
						</header>
						<Table
							className="tbl-normal"
							columns={columns}
							dataSource={visible}
							scroll={{ x: true }}
							locale={{ emptyText: <NotFound text={"درخواستی با نقص مدرک وجود ندارد."} /> }}
							pagination={{
								pageSize: PAGE_SIZE,
								align: "start",
								current: page,
								total,
								showSizeChanger: false,
								hideOnSinglePage: true,
								onChange: setPage,
							}}
						/>
					</section>
				</Content>
				<Loading isOpen={loading} />
			</div>
		</div>
	);
};

export default PendingDocsPage;
